import { useEffect, useState } from "react";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import { fetchCaseStudies } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

export default function CaseStudies() {
  const [items, setItems] = useState([]);
  useEffect(() => { fetchCaseStudies().then(setItems); }, []);

  return (
    <div data-testid="case-studies-page">
      <PageHero
        testId="case-hero"
        eyebrow="Case studies"
        title="What happens when cleanability is taken seriously."
        lead="A growing library of measurable, peer-reviewed transformations. Hospitals, terminals, factories, campuses. Each case documents the before, the intervention, and the operational outcome."
      />

      <section className="container-x section-y border-t border-border">
        <div className="space-y-6">
          {items.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.05}>
              <article data-testid={`case-${i}`} className="rounded-3xl border border-border bg-card p-8 md:p-12 hover-lift group">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-7">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="font-mono-mini text-[hsl(var(--accent))]">{c.sector}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">{c.location}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-mono-mini text-muted-foreground">{c.year}</span>
                    </div>
                    <h3 className="mt-4 font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-medium text-balance">
                      {c.title}
                    </h3>
                    <p className="mt-5 text-base text-muted-foreground leading-relaxed">{c.summary}</p>
                    <button data-testid={`case-read-${i}`} className="mt-8 inline-flex items-center gap-2 text-sm font-medium underline-link">
                      Read full case <ArrowUpRight className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="lg:col-span-5">
                    <div className="grid grid-cols-2 gap-3">
                      <Metric label="CI before" value={c.ci_before} />
                      <Metric label="CI after" value={c.ci_after} accent />
                      <Metric label="Cost reduction" value={`${c.cost_reduction}%`} />
                      <Metric label="Hygiene uplift" value={`${c.hygiene_uplift}%`} />
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-border overflow-hidden">
                      <div
                        className="h-full bg-[hsl(var(--accent))] transition-all"
                        style={{ width: `${c.ci_after}%` }}
                      />
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/40 p-5">
      <div className="font-mono-mini text-muted-foreground">{label}</div>
      <div className={`mt-2 font-display text-3xl font-medium tracking-tight ${accent ? "text-[hsl(var(--accent))]" : ""}`}>{value}</div>
    </div>
  );
}
